/**
 * What every rule pack shares: findings, rule descriptions, the context a check receives, and
 * helpers on circuits. A pack (RGIE, NF C 15-100, NEN 1010) is an ordered list of rules.
 */
import type { Element, Id, Project, Standard } from '@elec/domain';
import { analyse, pointKind, type Analysis, type CircuitInfo } from '@elec/analysis';
import { elementBounds, getSymbol, ROOM_TYPES } from '@elec/symbols';

export const fmt = (n: number, en = false) =>
  (Math.round(n * 10) / 10).toLocaleString(en ? 'en-GB' : 'fr-BE');

export type Severity = 'error' | 'warning' | 'info';
export type L = { fr: string; en: string; nl: string };

export interface Finding {
  ruleId: string;
  severity: Severity;
  message: L;
  /** Elements to select when the finding is clicked. */
  targets: Id[];
  sheetId?: Id;
}

export interface RuleInfo {
  id: string;
  severity: Severity;
  title: L;
  why: L;
  fix: L;
  /** false: from secondary sources, to be confirmed against the standard's text. */
  verified: boolean;
}

export interface Ctx {
  project: Project;
  a: Analysis;
  /** Room type of each plan point, by point code. */
  roomOf: Map<string, string>;
  planSheets: Id[];
  standard: Standard;
}

export type Check = (ctx: Ctx) => Array<Omit<Finding, 'ruleId' | 'severity'> & { severity?: Severity }>;

export interface Rule {
  info: RuleInfo;
  check: Check;
}

export const rule = (info: RuleInfo, check: Check): Rule => ({ info, check });

/** The same check with other texts or severity (a shared rule worded for another standard). */
export const variant = (r: Rule, patch: Partial<Omit<RuleInfo, 'id'>>): Rule => ({
  info: { ...r.info, ...patch },
  check: r.check,
});

export const byId = (rules: Rule[]): Record<string, Rule> =>
  Object.fromEntries(rules.map((r) => [r.info.id, r]));

export const WET_ROOMS = new Set(ROOM_TYPES.filter((r) => r.wet).map((r) => r.value));
export const WET_APPLIANCES = new Set(['iec.washing-machine', 'iec.dryer', 'iec.dishwasher']);
/** Fixed appliances that need a circuit of their own. */
export const DEDICATED = new Set([
  'iec.cooker',
  'iec.oven',
  'iec.washing-machine',
  'iec.dryer',
  'iec.dishwasher',
  'iec.water-heater',
  'iec.ev-charger',
  'iec.heat-pump',
]);
export const NO_EARTH = new Set(['iec.socket-noearth']);
export const RCD_SYMBOL = (e: Element) => e.symbolId.startsWith('iec.rcd') || e.symbolId === 'iec.rcbo';

/** Max protection rating per conductor section (copper, domestic). ⚠ to verify. */
export const MAX_RATING_A: Record<string, number> = {
  '1.5': 16,
  '2.5': 20,
  '4': 25,
  '6': 32,
  '10': 40,
  '16': 63,
};

export const name = (c: CircuitInfo) => c.code || c.device.tag;
export const ids = (c: CircuitInfo) => [c.device.id];
/** Section as written on a diagram: 2,5. */
export const section = (c: CircuitInfo) => String(c.cable.sectionMm2).replace('.', ',');

/** Sensitivity of an RCD element in mA (the symbol's default when not set). */
export function sensitivityMa(e: Element): number {
  const def = getSymbol(e.symbolId);
  const d = (def?.fields?.find((x) => x.key === 'sensitivityMa') as { default?: unknown } | undefined)
    ?.default;
  // '' = unknown (unreadable on an imported photo): never counts as 30 mA.
  if (e.props.sensitivityMa === '') return NaN;
  return Number(e.props.sensitivityMa ?? d ?? 30);
}

/** RCD type (AC, A, F, B) of an RCD element. */
export function rcdType(e: Element): string {
  const def = getSymbol(e.symbolId);
  const d = (def?.fields?.find((x) => x.key === 'rcdType') as { default?: unknown } | undefined)?.default;
  if (e.props.rcdType === '') return '?';
  return String(e.props.rcdType ?? d ?? 'A');
}

/** Why a circuit needs 30 mA (the wet appliance or room), in every language; null if it does not. */
export function isWet(ctx: Ctx, c: CircuitInfo): L | null {
  const lower = (l: L): L => ({ fr: l.fr.toLowerCase(), en: l.en.toLowerCase(), nl: l.nl.toLowerCase() });
  for (const p of c.points) {
    if (WET_APPLIANCES.has(p.element.symbolId)) return lower(getSymbol(p.element.symbolId)!.name);
    const room = ctx.roomOf.get(p.element.tag);
    if (room && WET_ROOMS.has(room)) return lower(ROOM_TYPES.find((r) => r.value === room)!.label);
  }
  return null;
}

/**
 * The room of each point drawn on a position plan, by point code (tag): the plan room whose
 * outline holds the point's centre, with its type (one of ROOM_TYPES) and the name written on it.
 */
export function pointRooms(
  project: Project,
  planSheets: Id[] = Object.values(project.sheets)
    .filter((s) => s.kind === 'position-plan')
    .map((s) => s.id),
): Map<string, { type: string; name: string }> {
  const rooms = Object.values(project.elements).filter(
    (e) => planSheets.includes(e.sheetId) && e.symbolId === 'plan.room',
  );
  const out = new Map<string, { type: string; name: string }>();
  for (const e of Object.values(project.elements)) {
    if (!planSheets.includes(e.sheetId) || !pointKind(e) || !e.tag) continue;
    const def = getSymbol(e.symbolId)!;
    const b = elementBounds(def, e);
    const cx = (b.minX + b.maxX) / 2;
    const cy = (b.minY + b.maxY) / 2;
    for (const r of rooms) {
      if (r.sheetId !== e.sheetId) continue;
      const rb = elementBounds(getSymbol('plan.room')!, r);
      if (cx >= rb.minX && cx <= rb.maxX && cy >= rb.minY && cy <= rb.maxY) {
        out.set(e.tag, { type: String(r.props.roomType ?? 'other'), name: String(r.props.name ?? '') });
        break;
      }
    }
  }
  return out;
}

function roomsOf(project: Project, planSheets: Id[]): Map<string, string> {
  return new Map([...pointRooms(project, planSheets)].map(([tag, r]) => [tag, r.type]));
}

const ORDER: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

/** Run a pack's rules on a project; errors first, then warnings, then information. */
export function runRules(
  rules: Rule[],
  project: Project,
  standard: Standard,
  analysis: Analysis = analyse(project),
): Finding[] {
  const planSheets = Object.values(project.sheets)
    .filter((s) => s.kind === 'position-plan')
    .map((s) => s.id);
  const ctx: Ctx = { project, a: analysis, planSheets, roomOf: roomsOf(project, planSheets), standard };
  const out: Finding[] = [];
  for (const r of rules)
    for (const f of r.check(ctx))
      out.push({ ruleId: r.info.id, ...f, severity: f.severity ?? r.info.severity });
  return out.sort((a, b) => ORDER[a.severity] - ORDER[b.severity]);
}

/**
 * Earth electrode requirement of a standard. RGIE: an electrode below 30 Ω. NF C 15-100 and
 * NEN 1010 (TT systems): RA × IΔn ≤ 50 V, with IΔn the least sensitive RCD; in France the
 * DSO's 500 mA branch breaker is always there, even when it is not drawn.
 */
export function earthElectrode(
  standard: Standard,
  earthing: string,
  rcdSensitivitiesMa: number[],
): { required: boolean; maxOhm: number } {
  if (standard === 'rgie') return { required: true, maxOhm: 30 };
  const floor = standard === 'nfc15100' ? 500 : 30;
  const idn = Math.max(floor, ...rcdSensitivitiesMa);
  return { required: earthing === 'TT', maxOhm: Math.floor(50_000 / idn) };
}

/**
 * Does the project's diagram need an earth electrode, and below which resistance (RGIE: always,
 * 30 Ω; NF C 15-100 / NEN 1010: in TT, from the least sensitive RCD)?
 */
export function earthNeeds(project: Project, a?: Analysis): { required: boolean; maxOhm: number } {
  const standard = project.meta.standard ?? 'rgie';
  return earthElectrode(standard, project.meta.earthingSystem, a ? circuitRcds(a).map(sensitivityMa) : []);
}

/** RCDs protecting at least one circuit, each once. */
export function circuitRcds(a: Analysis): Element[] {
  const out = new Map<Id, Element>();
  for (const c of a.circuits) for (const r of c.rcds) out.set(r.id, r);
  return [...out.values()];
}

/** Earth electrode rule for the standards where it follows from RA × IΔn ≤ 50 V. */
export function earthRule(id: string, info: Pick<RuleInfo, 'why' | 'fix' | 'verified'>): Rule {
  return rule(
    {
      id,
      severity: 'warning',
      title: { fr: 'Prise de terre', en: 'Earth electrode', nl: 'Aardelektrode' },
      ...info,
    },
    ({ project, a, standard }) => {
      const sens = circuitRcds(a).map(sensitivityMa);
      const { required, maxOhm } = earthElectrode(standard, project.meta.earthingSystem, sens);
      if (!required) return [];
      const e = Object.values(project.elements).filter((x) => x.symbolId === 'iec.earth-electrode');
      if (!e.length)
        return [
          {
            targets: [],
            message: {
              fr: 'Schéma TT : aucune prise de terre au schéma',
              en: 'TT system: no earth electrode on the diagram',
              nl: 'TT-stelsel: geen aardelektrode op het schema',
            },
          },
        ];
      return e.flatMap((x) => {
        const v = x.props.resistanceOhm;
        if (v === undefined || v === '')
          return [
            {
              targets: [x.id],
              sheetId: x.sheetId,
              message: {
                fr: `Résistance de la prise de terre non indiquée (max. ${maxOhm} Ω)`,
                en: `Earth electrode resistance not stated (max. ${maxOhm} Ω)`,
                nl: `Aardverspreidingsweerstand niet vermeld (max. ${maxOhm} Ω)`,
              },
            },
          ];
        if (Number(v) <= maxOhm) return [];
        return [
          {
            severity: 'error' as const,
            targets: [x.id],
            sheetId: x.sheetId,
            message: {
              fr: `Prise de terre ${v} Ω (max. ${maxOhm} Ω)`,
              en: `Earth electrode ${v} Ω (max. ${maxOhm} Ω)`,
              nl: `Aardelektrode ${v} Ω (max. ${maxOhm} Ω)`,
            },
          },
        ];
      });
    },
  );
}
